import React, { useState } from 'react';
import { ChevronLeft, LayoutList } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTaskCreateWizardForm } from '../../hooks/useTaskCreateWizardForm';
import { useCreateTask } from '../../hooks/useComponentTaskData';
import { useCreateComment } from '../../../comments/hooks/useComments';

export function TaskCreateWizard({ activeTask, systemUsers, user, onFinish }) {
    const { t: translate } = useTranslation('tasks');
    const [step, setStep] = useState(0);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [filteredUsers, setFilteredUsers] = useState(systemUsers);
    const [selectedUserIndex, setSelectedUserIndex] = useState(0);

    const createTaskMutation = useCreateTask();
    const createCommentMutation = useCreateComment();

    const form = useTaskCreateWizardForm();

    const handleKeyDown = async (e) => {
        if (e.key === 'Escape') {
            onFinish();
            return;
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            
            if (step === 0) {
                if (!title.trim()) {
                    form.setError('title', { type: 'required' });
                    return;
                }
                form.setValue('title', title);
                setStep(1);
            } else if (step === 1) {
                form.setValue('description', description);
                setStep(2);
                setFilteredUsers(systemUsers);
            } else if (step === 2 || step === 4) {
                if (filteredUsers.length > 0) {
                    const selectedUser = filteredUsers[selectedUserIndex];
                    if (step === 2) {
                        form.setValue('assignee', selectedUser.nick);
                        setStep(3);
                    } else {
                        form.setValue('supervisor', selectedUser.nick);
                        await submitWizard();
                    }
                }
            }
        } else if (e.key === 'ArrowDown') {
            if (step === 2 || step === 4) {
                e.preventDefault();
                setSelectedUserIndex(prev => Math.min(filteredUsers.length - 1, prev + 1));
            }
        } else if (e.key === 'ArrowUp') {
            if (step === 2 || step === 4) {
                e.preventDefault();
                setSelectedUserIndex(prev => Math.max(0, prev - 1));
            }
        }
    };

    const submitWizard = async () => {
        try {
            const formData = form.getValues();
            
            // Create the new task
            const newTask = await createTaskMutation.mutateAsync({
                title: formData.title,
                description: formData.description,
                assignee: formData.assignee,
                supervisor: formData.supervisor,
                duration_minutes: formData.duration,
                space_id: activeTask.space_id,
                stage: 'planeada',
            });

            // Create the comment ref
            await createCommentMutation.mutateAsync({
                document_id: activeTask.id,
                author_id: user.id,
                content: `<p>Modo Tarea Activo</p>`, // fallback
                metadata: {
                    is_task_ref: true,
                    type: 'task_ref',
                    ref_task_id: newTask.task?.id || newTask.id,
                    title: formData.title,
                    description: formData.description,
                    assignee: formData.assignee,
                    duration: formData.duration,
                }
            });

            onFinish();
        } catch (error) {
            console.error('Failed to create task from wizard', error);
        }
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        if (step === 0) {
            setTitle(val);
            if (form.formState.errors.title) form.clearErrors('title');
        } else if (step === 1) {
            setDescription(val);
        } else if (step === 2 || step === 4) {
            if (step === 2) form.setValue('assignee', val);
            if (step === 4) form.setValue('supervisor', val);
            
            const matches = systemUsers.filter(u => u.nick.toLowerCase().includes(val.toLowerCase()));
            setFilteredUsers(matches);
            setSelectedUserIndex(0);
        }
    };

    return (
        <div style={{ position: 'relative', marginTop: 'var(--space-1)', padding: 'var(--space-2)', border: '1px solid #3b82f6', borderRadius: 'var(--radius-md)', backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: '#3b82f6', fontSize: '12px', fontWeight: 600 }}>
                <LayoutList size={14} style={{ marginRight: '6px' }} />
                Creando Nueva Tarea
            </div>
            
            {(step === 2 || step === 4) && filteredUsers.length > 0 && (
                <ul style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, backgroundColor: '#222', borderRadius: 'var(--radius-md)', border: '1px solid #333', padding: 'var(--space-1)', margin: '0 0 var(--space-1) 0', listStyle: 'none', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                    {filteredUsers.slice(0, 3).map((u, i) => (
                        <li
                            key={u.id}
                            style={{ padding: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', backgroundColor: i === selectedUserIndex ? '#333' : 'transparent', borderRadius: 'var(--radius-sm)' }}
                            onClick={() => {
                                if (step === 2) {
                                    form.setValue('assignee', u.nick);
                                    setStep(3);
                                } else {
                                    form.setValue('supervisor', u.nick);
                                    submitWizard();
                                }
                            }}
                            onMouseEnter={() => setSelectedUserIndex(i)}
                        >
                            <img src={(u['link-profile-img-thumb'] || u['link-profile-img']) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.nick}`} alt={u.nick} loading="lazy" style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
                            <span style={{ color: '#fff', fontSize: '13px' }}>{u.nick}</span>
                        </li>
                    ))}
                </ul>
            )}

            {step === 3 && (
                <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, display: 'flex', gap: '6px', padding: 'var(--space-2)', margin: '0 0 var(--space-1) 0', backgroundColor: '#222', borderRadius: 'var(--radius-md)', border: '1px solid #333', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                    {[
                        { label: '5 min', val: 5 },
                        { label: '20 min', val: 20 },
                        { label: '40 min', val: 40 },
                        { label: '3 horas', val: 180 },
                        { label: '7 horas', val: 420 }
                    ].map(tag => (
                        <div
                            key={tag.val}
                            onClick={() => {
                                form.setValue('duration', tag.val);
                                setStep(4);
                                setFilteredUsers(systemUsers);
                                setSelectedUserIndex(0);
                            }}
                            style={{ padding: 'var(--space-1) var(--space-3)', backgroundColor: 'rgba(255,255,255,0.08)', color: '#fff', borderRadius: 'var(--radius-lg)', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.1)' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
                        >
                            {tag.label}
                        </div>
                    ))}
                </div>
            )}

            <div style={{ position: 'relative' }}>
                {step > 0 && (
                    <div
                        onClick={() => setStep(prev => Math.max(0, prev - 1))}
                        style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'rgba(59, 130, 246, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 5 }}
                        title="Atrás"
                    >
                        <ChevronLeft size={14} color="#3b82f6" />
                    </div>
                )}
                <input
                    type="text"
                    style={{ width: '100%', border: '1px solid #3b82f6', backgroundColor: '#111', color: '#fff', borderRadius: 'var(--radius-md)', padding: `10px 12px 10px ${step > 0 ? '36px' : '12px'}`, fontSize: '13px', outline: 'none' }}
                    placeholder={
                        step === 0 ? "Título de la tarea..." :
                        step === 1 ? "Descripción breve..." :
                        step === 2 ? "Asignar a..." :
                        step === 3 ? "Duración estimada..." : "Supervisor..."
                    }
                    value={
                        step === 0 ? title :
                        step === 1 ? description :
                        step === 2 ? form.watch('assignee') :
                        step === 3 ? "Selecciona duración..." : form.watch('supervisor')
                    }
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    readOnly={step === 3}
                />
            </div>
            
            {step === 0 && form.formState.errors.title && (
                <div style={{ marginTop: '4px', fontSize: '10px', color: '#f87171' }}>
                    El título es requerido
                </div>
            )}
            
            <div style={{ marginTop: '8px', height: '3px', backgroundColor: '#333', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', backgroundColor: '#3b82f6', width: `${(step / 4) * 100}%`, transition: 'width 0.3s ease' }} />
            </div>
        </div>
    );
}
